<template>
  <div class="col-12 mt-3">
    <div class="form-group">
      <label for="sms-panel-api-url">آدرس API ملی پیامک</label>
      <input
          id="sms-panel-api-url"
          type="text"
          class="form-control"
          dir="ltr"
          v-model="settings.api_url"
          @input="emitValue"
          placeholder="https://console.melipayamak.com/api/send/shared/YOUR_TOKEN"
      />
    </div>

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="mb-0">قالب‌ها</h6>
      <button type="button" class="btn btn-sm btn-primary rounded-pill" @click="addTemplate">
        افزودن قالب
      </button>
    </div>

    <div
        v-for="(item, index) in templateItems"
        :key="item.localKey"
        class="border rounded p-3 mb-3"
    >
      <div class="row">
        <div class="col-lg-5">
          <div class="form-group">
            <label>قالب سیستم</label>
            <select
                class="custom-select"
                :key="`template-select-${item.localKey}-${templatesLoaded}`"
                v-model="item.templateId"
                @change="onTemplateChange(index)"
            >
              <option value="">انتخاب قالب</option>
              <option
                  v-for="template in availableTemplates(item.templateId)"
                  :key="template.id"
                  :value="String(template.id)"
              >
                {{ template.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="col-lg-5">
          <div class="form-group">
            <label>شناسه قالب ملی پیامک (bodyId)</label>
            <input
                type="number"
                class="form-control"
                dir="ltr"
                v-model.number="item.bodyId"
                @input="syncTemplates"
                placeholder="524"
            />
          </div>
        </div>

        <div class="col-lg-2 d-flex align-items-end">
          <button
              type="button"
              class="btn btn-outline-danger btn-sm mb-3 w-100"
              @click="removeTemplate(index)"
          >
            حذف
          </button>
        </div>
      </div>

      <div v-if="item.templateId && item.args.length" class="mt-2">
        <label class="d-block mb-2">ترتیب متغیرها</label>
        <small class="text-muted d-block mb-2">
          ترتیب ارسال به ملی پیامک مهم است. فقط ترتیب را تغییر دهید.
        </small>

        <div
            v-for="(arg, argIndex) in item.args"
            :key="`${item.localKey}-${arg}`"
            class="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2"
        >
          <span dir="ltr">{{ arg }}</span>
          <div>
            <button
                type="button"
                class="btn btn-sm btn-light ml-1"
                :disabled="argIndex === 0"
                @click="moveArg(index, argIndex, -1)"
            >
              <b-icon icon="arrow-up"></b-icon>
            </button>
            <button
                type="button"
                class="btn btn-sm btn-light"
                :disabled="argIndex === item.args.length - 1"
                @click="moveArg(index, argIndex, 1)"
            >
              <b-icon icon="arrow-down"></b-icon>
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="item.templateId" class="text-muted mt-2">
        متغیری در این قالب یافت نشد.
      </div>
    </div>

    <div v-if="!templateItems.length" class="text-muted mb-3">
      هنوز قالبی اضافه نشده است.
    </div>
  </div>
</template>

<script>
export default {
  name: "SmsPanelSettings",
  props: {
    value: {
      default: null
    }
  },
  data() {
    return {
      smsTemplates: [],
      templatesLoaded: false,
      settings: {
        api_url: '',
        templates: {}
      },
      templateItems: [],
      localKeyCounter: 0,
      hydrating: false,
    }
  },
  methods: {
    nextLocalKey() {
      this.localKeyCounter += 1
      return `tpl-${this.localKeyCounter}`
    },
    parseValue(value) {
      if (!value) {
        return {api_url: '', templates: {}}
      }

      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value)
          return {
            api_url: parsed.api_url || '',
            templates: parsed.templates || {}
          }
        } catch (e) {
          return {api_url: '', templates: {}}
        }
      }

      if (typeof value === 'object') {
        return {
          api_url: value.api_url || '',
          templates: value.templates || {}
        }
      }

      return {api_url: '', templates: {}}
    },
    normalizeSettings(value) {
      const parsed = this.parseValue(value)
      const templates = {}

      Object.keys(parsed.templates || {}).forEach(templateId => {
        const config = parsed.templates[templateId] || {}
        templates[String(templateId)] = {
          bodyId: config.bodyId === '' || config.bodyId == null ? '' : Number(config.bodyId),
          args: Array.isArray(config.args) ? [...config.args] : []
        }
      })

      return {
        api_url: parsed.api_url || '',
        templates
      }
    },
    extractTemplateVariables(text) {
      const regex = /\[([^\]]+)\]/g
      const matches = []
      let match
      while ((match = regex.exec(text || '')) !== null) {
        if (matches.indexOf(match[1]) === -1) {
          matches.push(match[1])
        }
      }
      return matches
    },
    findSmsTemplate(templateId) {
      return this.smsTemplates.find(item => String(item.id) === String(templateId))
    },
    resolveArgs(templateId, savedArgs = []) {
      const template = this.findSmsTemplate(templateId)
      if (!template) {
        return Array.isArray(savedArgs) ? [...savedArgs] : []
      }

      const variables = this.extractTemplateVariables(template.template || '')
      if (!variables.length) {
        return []
      }

      const ordered = []
      ;(savedArgs || []).forEach(arg => {
        if (variables.indexOf(arg) > -1 && ordered.indexOf(arg) === -1) {
          ordered.push(arg)
        }
      })

      variables.forEach(arg => {
        if (ordered.indexOf(arg) === -1) {
          ordered.push(arg)
        }
      })

      return ordered
    },
    hydrateFromValue(value) {
      this.hydrating = true
      this.settings = this.normalizeSettings(value)
      this.templateItems = Object.keys(this.settings.templates || {}).map(templateId => {
        const config = this.settings.templates[templateId] || {}
        return {
          localKey: this.nextLocalKey(),
          templateId: String(templateId),
          bodyId: config.bodyId === '' || config.bodyId == null ? '' : Number(config.bodyId),
          args: this.resolveArgs(templateId, config.args || [])
        }
      })
      this.$nextTick(() => {
        this.hydrating = false
      })
    },
    availableTemplates(currentTemplateId) {
      const selectedIds = this.templateItems
          .map(item => String(item.templateId))
          .filter(id => id && id !== String(currentTemplateId || ''))

      return this.smsTemplates.filter(template => {
        return selectedIds.indexOf(String(template.id)) === -1
      })
    },
    addTemplate() {
      this.templateItems.push({
        localKey: this.nextLocalKey(),
        templateId: '',
        bodyId: '',
        args: []
      })
    },
    removeTemplate(index) {
      this.templateItems.splice(index, 1)
      this.syncTemplates()
    },
    onTemplateChange(index) {
      const item = this.templateItems[index]
      item.templateId = item.templateId ? String(item.templateId) : ''
      item.args = item.templateId ? this.resolveArgs(item.templateId, []) : []
      this.syncTemplates()
    },
    moveArg(templateIndex, argIndex, direction) {
      const args = this.templateItems[templateIndex].args
      const targetIndex = argIndex + direction
      if (targetIndex < 0 || targetIndex >= args.length) {
        return
      }

      const temp = args[argIndex]
      this.$set(args, argIndex, args[targetIndex])
      this.$set(args, targetIndex, temp)
      this.syncTemplates()
    },
    syncTemplates() {
      const templates = {}
      this.templateItems.forEach(item => {
        if (!item.templateId) {
          return
        }
        templates[String(item.templateId)] = {
          bodyId: item.bodyId === '' || item.bodyId == null ? '' : Number(item.bodyId),
          args: [...item.args]
        }
      })
      this.settings.templates = templates
      this.emitValue()
    },
    emitValue() {
      if (this.hydrating) {
        return
      }
      this.$emit('input', {
        api_url: this.settings.api_url || '',
        templates: this.settings.templates || {}
      })
    },
    isSameValue(a, b) {
      return JSON.stringify(this.normalizeSettings(a)) === JSON.stringify(this.normalizeSettings(b))
    },
    async getSmsTemplates() {
      const templates = await this.$store.dispatch('Sms/getSmsTemplates')
      this.smsTemplates = (templates && templates.data && templates.data.data && templates.data.data.data) || []
      this.templatesLoaded = true
    },
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.hydrating) {
          return
        }
        if (!this.isSameValue(newValue, {
          api_url: this.settings.api_url || '',
          templates: this.settings.templates || {}
        })) {
          this.hydrateFromValue(newValue)
        }
      },
      deep: true
    }
  },
  async mounted() {
    await this.getSmsTemplates()
    this.hydrateFromValue(this.value)
  }
}
</script>

<style scoped>
</style>
